import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from '../../../components/bootstrap/Button';
import Card, {
	CardBody,
	CardHeader,
	CardLabel,
	CardActions,
	CardTitle,
	CardSubTitle,
	CardFooter,
	CardFooterLeft,
	CardFooterRight,
} from '../../../components/bootstrap/Card';
import Icon from '../../../components/icon/Icon';
import AuthContext from '../../../contexts/authContext';
import { MailMenu } from '../../../gestioneerMenu';
import useTourStep from '../../../hooks/useTourStep';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Droit from '../../../assets/img/scene2.webp';
import SubHeader, {
	SubHeaderLeft,
	SubHeaderRight,
	SubheaderSeparator,
} from '../../../layout/SubHeader/SubHeader';
import SignaturePopup from './SignaturePopup';
import { isMobile } from 'react-device-detect';
import { useMediaQuery } from 'react-responsive';
import { formatCurrency } from '../../comptabilite/utils/functions';
import { getPublicDevis, refusPublicDevis } from '../../../apis/backend/apiPublic';
import { Badge } from 'react-bootstrap';

export default function AcceptDevisMail() {
	const { id, token } = useParams(0);
	const [devis, setDevis] = useState();
	const [devisLines, setDevisLines] = useState();
	const [openSignature, setOpenSignature] = useState(false);
	const [readDevis, setReadDevis] = useState(false);
	const [spinner, setSpinner] = useState(false);
	const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });

	const loadData = useCallback(async () => {
		try {
			const response = await getPublicDevis(id, token);
			const data = response.data;
			if (response?.success && data) {
				setReadDevis(true);
				setDevis(data);
				setDevisLines(data?.lignes);
			} else {
				setReadDevis(false);
			}
		} catch (err) {
			setReadDevis(false);
		} finally {
			setSpinner(true);
		}
	}, [id]);

	const handleCancel = async (raison) => {
		await refusPublicDevis(id, token, { raisonRefus: raison });
		loadData();
	};

	useEffect(() => {
		loadData();
	}, [id, token]);

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		return new Date(dateString).toLocaleDateString('fr-FR', {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
		});
	};

	return (
		<PageWrapper title={MailMenu?.acceptDevis?.text} isProtected={false}>
			{!!readDevis && (
				<SubHeader>
					<SubHeaderLeft>
						<img
							src={`/coreService/${devis?.companyId?.logo}`}
							alt=''
							className='p-4'
							width='300'
							height='100'
						/>
						<SubheaderSeparator />
					</SubHeaderLeft>
					<SubHeaderRight className='d-block text-end'>
						<div className='fs-4 fw-bold'>{devis?.companyId?.name}</div>
						<div className='text-muted'>{devis?.companyId?.email}</div>
						<div className='text-muted'>{devis?.companyId?.phone}</div>
						<div></div>
					</SubHeaderRight>
				</SubHeader>
			)}

			<Page className='mt-3'>
				{!!spinner && (
					<>
						{readDevis ? (
							<>
								<div className='row'>
									{/* Left Column - Buttons & Info */}
									<div className='col-lg-3'>
										{/* Total Amount Card */}
										<Card shadow='sm' className='mb-4'>
											<CardBody>
												<div className='border-bottom text-center mb-4 pb-3'>
													<div className='text-muted mb-2'>
														Notre offre
													</div>
													<h1 className='display-4 fw-bold text-primary mb-0'>
														{formatCurrency(devis?.totalTTC, 'EUR')}
													</h1>
													<small className='text-muted'>
														({formatCurrency(devis?.totalHT, 'EUR')} HT)
													</small>
												</div>

												{/* Action Buttons */}
												<div className='text-center'>
													{(devis?.statut == 'brouillon' ||
														devis?.statut == 'envoyé') && (
															<Button
																icon='Draw'
																size='lg'
																color='success'
																className='w-100 mb-3 fw-bold py-3'
																onClick={() => setOpenSignature(true)}>
																Accepter et signer
															</Button>
														)}

													<div className='d-flex gap-2'>
														<Button
															tag='a'
															color='info'
															className='flex-fill'
															icon='DownloadForOffline'
															href={`/coreService/document-layouts/client/download/Devis/${id}`}
															target='_blank'>
															Télécharger
														</Button>
														{/* <Button
															tag='a'
															color='info'
															className='flex-fill'
															icon='Print'
															href={`/coreService/document-layouts/download/Devis/${id}`}
															target='_blank'>
															Imprimer
														</Button> */}
													</div>
												</div>
											</CardBody>
										</Card>
										{(devis?.statut == 'brouillon' ||
											devis?.statut == 'envoyé') && (
												<Card shadow='sm' className='mb-4'>
													<CardBody>
														<div className='border-bottom text-center mb-4 pb-3'>
															<div className='text-muted mb-2'>
																Si notre offre ne vous convienne pas,
																merci de nous indiquer la raison. Cela
																nous aidera à mieux vous comprendre.
															</div>
														</div>

														{/* Action Button */}
														<div className='text-center'>
															<Button
																color='danger'
																isLight
																icon='close'
																className='w-100 mb-3 fw-bold'
																onClick={() => {
																	const raison = prompt(
																		'Dites nous pourquoi vous refusez ce devis afin de nous aider à améliorer nos services :',
																	);
																	if (raison) handleCancel(raison);
																}}>
																Refuser le devis
															</Button>
														</div>
													</CardBody>
												</Card>
											)}

										{/* Signed Info */}
										{devis?.signature && devis?.statut == 'accepté' && (
											<Card shadow='sm' className='border-success'>
												<CardHeader className='bg-l25-success bg-opacity-10'>
													<CardLabel icon='Draw' iconColor='success'>
														<CardTitle className='text-success'>
															Déjà signé
														</CardTitle>
													</CardLabel>
												</CardHeader>
												<CardBody>
													<div className='text-center'>
														<Icon
															icon='CheckCircle'
															size='3x'
															color='success'
															className='mb-3'
														/>
														<p className='mb-2 fw-bold'>
															Signé par {devis.clientId?.nom}
														</p>
														<p className='mb-1 text-muted'>
															Le {formatDate(devis.signedOn)}
														</p>
													</div>
												</CardBody>
											</Card>
										)}

										{devis?.raisonRefus && devis?.statut == 'refusé' && (
											<Card shadow='sm' className='border-danger'>
												<CardHeader className='bg-l25-dangerbg-opacity-10'>
													<CardLabel>
														<CardTitle className='text-danger'>
															Déjà refusé
														</CardTitle>
													</CardLabel>
												</CardHeader>
												<CardBody>
													<div className='text-center'>
														<Icon
															icon='close'
															size='3x'
															color='danger'
															className='mb-3'
														/>
														<p className='mb-2 fw-bold'>
															Refusé par {devis.clientId?.nom}
														</p>
														<p className='mb-1 text-muted'>
															Raison : {devis?.raisonRefus}
														</p>
													</div>
												</CardBody>
											</Card>
										)}
									</div>

									{/* Right Column - Devis Details Table */}
									<div className='col-lg-9'>
										<Card shadow='sm' className='h-100'>
											<CardHeader>
												<CardLabel icon='Assignment' iconColor='primary'>
													<CardTitle>Détails du devis</CardTitle>
													<CardSubTitle>{devis?.name}</CardSubTitle>
												</CardLabel>
											</CardHeader>

											<CardBody className='table-responsive'>
												<div className='row g-3 mb-5'>
													<div className='col-12'>
														<span className='text-muted me-3'>
															Référence du devis :
														</span>
														<span className='fw-bold'>
															{devis?.reference}
														</span>
													</div>
													<div className='col-12'>
														<span className='text-muted me-3'>
															Statut du devis :
														</span>
														<span className='fw-bold'>
															{devis?.statut == 'accepté'
																? 'Accepté'
																: devis?.statut == 'refusé'
																	? 'Refusé'
																	: 'En cours'}
														</span>
													</div>
													<div className='col-12'>
														<span className='text-muted me-3'>
															Date du devis :
														</span>
														<span className='fw-bold'>
															{formatDate(devis?.createdAt)}
														</span>
													</div>
													<div className='col-12'>
														<span className='text-muted me-3'>
															Nom du client :
														</span>
														<span className='fw-bold'>
															{devis?.clientId?.nom}
														</span>
													</div>
												</div>
												<table className='table table-modern table-hover'>
													<thead>
														<tr>
															<th scope='col'>Article</th>

															<th scope='col'>Prix</th>
															<th scope='col'>Qté</th>

															<th scope='col'>Total</th>
														</tr>
													</thead>
													{devisLines?.length > 0 && (
														<tbody>
															{devisLines.map((line) => {
																if (line?.type == 'note') {
																	return (
																		<tr>
																			<td colSpan={4}>
																				<div>
																					{line?.nom}
																				</div>
																			</td>
																		</tr>
																	);
																} else {
																	return (
																		<tr>
																			<td>

																				{!isMobile && (
																					<span
																						dangerouslySetInnerHTML={{
																							__html: line?.description.replace(
																								/\n/g,
																								'<br>',
																							),
																						}}
																					/>
																				)}
																			</td>

																			<td>
																				<span>
																					{line?.prixUnitaireHT?.toLocaleString(
																						'fr-FR',
																						{
																							style: 'currency',
																							currency:
																								'EUR',
																						},
																					)}
																				</span>
																			</td>
																			<td>
																				<span>
																					{line?.quantite}
																				</span>
																			</td>

																			<td>
																				<span>
																					{
																						// @ts-ignore
																						(
																							line?.prixUnitaireHT *
																							line?.quantite
																						)?.toLocaleString(
																							'fr-FR',
																							{
																								style: 'currency',
																								currency:
																									'EUR',
																							},
																						)
																					}
																				</span>
																			</td>
																		</tr>
																	);
																}
															})}
														</tbody>
													)}
												</table>
											</CardBody>
											<CardFooter borderColor={'light'} borderSize={2}>
												<CardFooterLeft className='d-md-flex d-sm-block'>
													<div className='flex-grow-1 ms-3 mb-3 d-flex col-auto'>
														<div className='text-primary fw-bold'>
															Total H.T:{' '}
														</div>
														<div className='ms-2 fw-bold fs-6 mb-0'>
															{devis?.totalHT?.toLocaleString(
																'fr-FR',
																{
																	style: 'currency',
																	currency: 'EUR',
																},
															)}
														</div>
													</div>
													<div className='flex-grow-1 ms-3 mb-3 d-flex col-auto'>
														<div className='text-primary fw-bold'>
															Remise:{' '}
														</div>
														<div className='ms-2 fw-bold fs-6 mb-0'>
															{devis?.montantRemise?.toLocaleString(
																'fr-FR',
																{
																	style: 'currency',
																	currency: 'EUR',
																},
															)}
														</div>
													</div>
													<div className='flex-grow-1 ms-3 mb-3 d-flex col-auto'>
														<div className='text-primary fw-bold'>
															TVA:
														</div>
														<div className='ms-2 fw-bold fs-6 mb-0'>
															{devis?.montantTVA?.toLocaleString(
																'fr-FR',
																{
																	style: 'currency',
																	currency: 'EUR',
																},
															)}
														</div>
													</div>

													<div className='flex-grow-1 ms-3 mb-3 d-flex col-auto'>
														<div className='text-primary fw-bold'>
															Total T.T.C:
														</div>
														<div className='ms-2 fw-bold fs-6 mb-0'>
															{devis?.totalTTC?.toLocaleString(
																'fr-FR',
																{
																	style: 'currency',
																	currency: 'EUR',
																},
															)}
														</div>
													</div>
												</CardFooterLeft>
											</CardFooter>
										</Card>
									</div>
								</div>

								{!!openSignature && (
									<SignaturePopup
										setOpenSignature={setOpenSignature}
										devis={devis}
										token={token}
										setDevis={setDevis}
										loadData={loadData}
									/>
								)}
							</>
						) : (
							<div className='d-flex justify-content-center align-items-center'>
								{/* Card with error message */}
								<Card
									className='text-center shadow-sm'
									style={{ width: '100%', maxWidth: '600px' }}>
									<CardBody>
										{/* Icon or message */}
										<Icon
											name='exclamation-triangle'
											size='2x'
											color='warning'
										/>
										<CardTitle className='text-danger mt-3'>
											Désolé, ce lien a expiré
										</CardTitle>
										<span className='text-muted'>
											Le lien que vous avez suivi n'est plus valide. Veuillez
											vérifier si vous avez entré l'URL correcte ou
											contactez-nous pour plus d'informations.
										</span>

										{/* Optional: Action button to redirect */}
									</CardBody>
								</Card>
							</div>
						)}
					</>
				)}
			</Page>
		</PageWrapper>
	);
}
